import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import mercadoLibreService from '../../../services/mercadoLibreService';

const OAuthFlow = ({
  clientId,
  market = 'MCO',
  onAuthSuccess,
  onAuthError,
  isEnabled = false,
  onRefreshToken,
  isRefreshing = false
}) => {
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [authWindow, setAuthWindow] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('idle'); // idle, connecting, success, error
  const [debugInfo, setDebugInfo] = useState([]);
  const [retryAttempts, setRetryAttempts] = useState(0);
  const [authCompleted, setAuthCompleted] = useState(false);
  const authCompletedRef = useRef(false);
  useEffect(() => { authCompletedRef.current = authCompleted; }, [authCompleted]);
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);

  // Deduplicación: rastrear códigos OAuth ya procesados durante toda la sesión
  // NO se limpian inmediatamente para evitar doble intercambio por race condition
  const processedOAuthCodesRef = useRef(new Set());

  // Agregar el servicio globalmente para los botones
  useEffect(() => {
    window.mercadoLibreService = mercadoLibreService;
  }, []);

  // Add debug logging function
  const addDebugLog = (message, type = 'info') => {
    const timestamp = new Date()?.toLocaleTimeString();
    setDebugInfo(prev => [...prev?.slice(-9), { timestamp, message, type }]);
    console.log(`[OAuth Debug] ${timestamp}: ${message}`);
  };

  // Enhanced popup message handler
  useEffect(() => {
    const handleMessage = (event) => {
      // Aceptar mensajes de cualquier origen y validar con 'state' en el intercambio
      // Esto evita falsos negativos por diferencias www/apex o proxies
      addDebugLog(`Message received from ${event?.origin}: ${JSON.stringify(event?.data)}`, 'info');

      if (event?.data?.type === 'OAUTH_CALLBACK') {
        // Marcar completado ANTES de procesar para evitar condiciones de carrera
        authCompletedRef.current = true;
        setAuthCompleted(true);
        // Limpiar timers de popup
        if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
        if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
        handleOAuthCallback(event?.data);
      }
    };

    const handleStorage = (e) => {
      try {
        if (e?.key !== 'ml_oauth_result' || !e?.newValue) return;
        const record = JSON.parse(e.newValue);
        addDebugLog('Storage event received for ml_oauth_result', 'info');
        const expectedState = sessionStorage.getItem('oauth_state');
        if (record?.state && expectedState && record.state !== expectedState) {
          addDebugLog('Ignored storage result due to state mismatch', 'warning');
          return;
        }
        authCompletedRef.current = true;
        setAuthCompleted(true);
        if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
        if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
        handleOAuthCallback(record);
      } catch (err) {
        addDebugLog('Error handling storage event: ' + err?.message, 'error');
      }
    };

    window.addEventListener('message', handleMessage);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  const handleOAuthCallback = async (data) => {
    addDebugLog('Processing OAuth callback...', 'info');

    try {
      if (data?.error) {
        const errorMsg = data?.error_description || data?.error;
        addDebugLog(`OAuth error received: ${errorMsg}`, 'error');
        setConnectionStatus('error');
        onAuthError(`Error de MercadoLibre: ${errorMsg}`);
        authCompletedRef.current = true; setAuthCompleted(true);
        return;
      }

      if (data?.code && data?.state) {
        // Guard: evitar procesar el MISMO authorization code dos veces
        if (processedOAuthCodesRef.current.has(data.code)) {
          addDebugLog(`Duplicate OAuth code detected: ${data.code?.substring(0, 10)}... - ignoring`, 'warning');
          return; // Ignorar silenciosamente el callback duplicado
        }

        // Marcar este código como procesado ANTES del await para evitar race condition
        processedOAuthCodesRef.current.add(data.code);
        addDebugLog('Auth code and state received', 'success');

        const tokenData = await verifyConnection(data?.code, data?.state);
        setConnectionStatus('success');
        addDebugLog('OAuth flow completed successfully', 'success');
        onAuthSuccess(tokenData);
        authCompletedRef.current = true; setAuthCompleted(true);
      } else {
        addDebugLog('Invalid callback data received', 'error');
        setConnectionStatus('error');
        onAuthError('Datos de callback inválidos recibidos');
        authCompletedRef.current = true; setAuthCompleted(true);
      }
    } catch (error) {
      addDebugLog(`Callback processing error: ${error?.message}`, 'error');
      setConnectionStatus('error');
      onAuthError(error?.message || 'Error al procesar el callback de OAuth');
      authCompletedRef.current = true; setAuthCompleted(true);
    } finally {
      if (authWindow) {
        authWindow?.close();
        setAuthWindow(null);
      }
      setIsAuthenticating(false);
    }
  };

  // PKCE helpers
  const base64UrlEncode = (arrayBuffer) => {
    const bytes = new Uint8Array(arrayBuffer);
    let str = '';
    for (let i = 0; i < bytes.byteLength; i++) str += String.fromCharCode(bytes[i]);
    return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  };

  const generateCodeVerifier = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    let verifier = '';
    for (let i = 0; i < 64; i++) verifier += chars.charAt(Math.floor(Math.random() * chars.length));
    return verifier;
  };

  const createPKCE = async () => {
    const verifier = generateCodeVerifier();
    const data = new TextEncoder().encode(verifier);
    const digest = await window.crypto.subtle.digest('SHA-256', data);
    const challenge = base64UrlEncode(digest);
    // Persistir para el intercambio del backend
    localStorage.setItem('ml_oauth_code_verifier', verifier);
    return { verifier, challenge };
  };

  const getAuthUrl = async () => {
    const baseUrls = {
      MLA: 'https://auth.mercadolibre.com.ar',
      MLM: 'https://auth.mercadolibre.com.mx',
      MCO: 'https://auth.mercadolibre.com.co',
      MLC: 'https://auth.mercadolibre.com.cl',
      MLB: 'https://auth.mercadolibre.com.br',
      MLU: 'https://auth.mercadolibre.com.uy'
    };

    // Usar callback dedicado configurado en la app
    const redirectUri = (import.meta.env?.VITE_MERCADOLIBRE_REDIRECT_URI)
      || (window.location?.origin + '/oauth/callback.html');
  const state = `oauth_${Date.now()}_${Math.random()?.toString(36)?.substring(7)}`;

    addDebugLog(`Building auth URL for market: ${market}`, 'info');
    addDebugLog(`Redirect URI: ${redirectUri}`, 'info');
    addDebugLog(`Client ID: ${clientId ? clientId?.substring(0, 10) + '...' : 'NO CLIENT ID'}`, 'info');

    // Generar PKCE y guardar verifier
    const { challenge } = await createPKCE();
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: clientId,
      redirect_uri: redirectUri,
      state: state,
      code_challenge: challenge,
      code_challenge_method: 'S256'
    });

    // Store state for verification
    sessionStorage.setItem('oauth_state', state);
    sessionStorage.setItem('oauth_redirect_uri', redirectUri);
    sessionStorage.setItem('oauth_client_id', clientId);

  const authUrl = `${baseUrls?.[market]}/authorization?${params?.toString()}`;
    addDebugLog(`Final auth URL: ${authUrl}`, 'info');

    return authUrl;
  };

  const verifyConnection = async (authCode, state) => {
    try {
      addDebugLog('Starting connection verification...', 'info');

      // Verify state parameter
      const storedState = sessionStorage.getItem('oauth_state');
      if (state !== storedState) {
        addDebugLog(`State mismatch: expected ${storedState}, got ${state}`, 'error');
        throw new Error('Estado de OAuth inválido - posible ataque CSRF');
      }
      addDebugLog('State verification passed', 'success');

      // Enhanced auth code validation
      if (!authCode || typeof authCode !== 'string' || authCode?.length < 5) {
        addDebugLog(`Invalid auth code: ${authCode}`, 'error');
        throw new Error('Código de autorización inválido recibido de MercadoLibre');
      }
      addDebugLog(`Auth code received: ${authCode?.substring(0, 10)}...`, 'success');

      // Intercambio REAL vía nuestro backend (con client_secret seguro)
      addDebugLog('Attempting token exchange via backend...', 'info');
      const storedRedirectUri = sessionStorage.getItem('oauth_redirect_uri');

      const apiBaseUrl = import.meta.env?.VITE_API_BASE_URL || window.location.origin;
      const codeVerifier = localStorage.getItem('ml_oauth_code_verifier');
      const tokenResponse = await fetch(`${apiBaseUrl}/api/mercadolibre/oauth/exchange`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: authCode, redirectUri: storedRedirectUri, codeVerifier })
      });

      const payload = await tokenResponse.json().catch(() => ({}));
      if (!tokenResponse.ok || !payload?.success || !payload?.data) {
        const msg = payload?.message || `HTTP ${tokenResponse.status}`;
        addDebugLog(`Token exchange failed: ${msg}`, 'error');
        throw new Error(msg);
      }

      const tokenData = payload.data;
      // Guardar tokens usando el servicio central
      try {
        mercadoLibreService.storeTokens(
          tokenData.access_token,
          tokenData.refresh_token,
          tokenData.user_id
        );
      } catch (e) {
        addDebugLog('Warning: could not persist tokens: ' + e?.message, 'warning');
      }

      const enhancedTokenData = {
        ...tokenData,
        obtained_at: new Date()?.toISOString(),
        expires_at: new Date(Date.now() + (tokenData?.expires_in * 1000))?.toISOString()
      };
      addDebugLog('Token exchange successful via backend', 'success');
      return enhancedTokenData;

    } catch (error) {
      addDebugLog(`Verification error: ${error?.message}`, 'error');
      console.error('OAuth verification error:', error);
      throw error;
    } finally {
      // Clean up stored data
      sessionStorage.removeItem('oauth_state');
      sessionStorage.removeItem('oauth_redirect_uri');
      sessionStorage.removeItem('oauth_client_id');
      localStorage.removeItem('ml_oauth_result');
      // mantener temporalmente el code_verifier por si hay reintentos manuales
    }
  };

  const handleOAuthFlow = async () => {
    if (!clientId || !clientId?.trim()) {
      addDebugLog('OAuth flow aborted: Missing Client ID', 'error');
      onAuthError('Client ID es requerido para la autenticación');
      return;
    }

    if (!market) {
      addDebugLog('OAuth flow aborted: Missing market', 'error');
      onAuthError('Mercado es requerido para la autenticación');
      return;
    }

    // Iniciar nuevo flujo OAuth: limpiar códigos procesados del intento anterior
    // Así permitimos que un nuevo authorization code se procese normalmente
    processedOAuthCodesRef.current.clear();

    addDebugLog(`Starting OAuth flow (attempt ${retryAttempts + 1})...`, 'info');
    setIsAuthenticating(true);
    setConnectionStatus('connecting');

    try {
  const authUrl = await getAuthUrl();

      addDebugLog('Opening popup window...', 'info');

      // Simple popup configuration
      const popupFeatures = [
        'width=600',
        'height=700',
        'scrollbars=yes',
        'resizable=yes',
        'left=' + (window.screen?.width / 2 - 300),
        'top=' + (window.screen?.height / 2 - 350)
      ]?.join(',');

      const popup = window.open(
        authUrl,
        'mercadolibre_oauth_' + Date.now(),
        popupFeatures
      );

      if (!popup || popup?.closed || typeof popup?.closed === 'undefined') {
        addDebugLog('Popup blocked by browser', 'error');
        setIsAuthenticating(false);
        setConnectionStatus('error');
        onAuthError('Popup bloqueado. Por favor, permite popups para este sitio y vuelve a intentar.');
        return;
      }

      setAuthWindow(popup);
      addDebugLog('Popup opened successfully', 'success');

      // Simplified popup monitoring - restore previous working behavior
  let checkInterval;
  let timeoutId;

      const cleanup = () => {
        if (checkInterval) clearInterval(checkInterval);
        if (timeoutId) clearTimeout(timeoutId);
        if (popup && !popup?.closed) {
          popup?.close();
        }
        setAuthWindow(null);
        setIsAuthenticating(false);
      };

      // Set timeout for the entire process
      timeoutId = setTimeout(() => {
        addDebugLog('OAuth flow timed out', 'error');
        cleanup();
        setConnectionStatus('error');
        onAuthError('Tiempo de espera agotado. El proceso de autorización tomó demasiado tiempo.');
      }, 5 * 60 * 1000); // 5 minutes
      // Guardar refs
      timeoutRef.current = timeoutId;

      // Monitoreo: al cerrar popup, si ya completamos, no hacer nada; sin simulaciones
      checkInterval = setInterval(async () => {
        try {
          // Check if popup is closed
          if (popup?.closed) {
            addDebugLog('Popup window closed', 'info');
            clearInterval(checkInterval);
            clearTimeout(timeoutId);
            intervalRef.current = null; timeoutRef.current = null;
            setIsAuthenticating(false);
            setAuthWindow(null);

            if (authCompletedRef.current) {
              addDebugLog('Auth already completed via message; skipping URL check.', 'info');
              return;
            }

            // Fallback: revisar localStorage por ml_oauth_result
            try {
              const raw = localStorage.getItem('ml_oauth_result');
              if (raw) {
                const record = JSON.parse(raw);
                const expectedState = sessionStorage.getItem('oauth_state');
                if (record?.state && expectedState && record.state === expectedState) {
                  addDebugLog('Using ml_oauth_result from storage after popup close.', 'success');
                  authCompletedRef.current = true; setAuthCompleted(true);
                  await handleOAuthCallback(record);
                  return;
                }
              }
            } catch (e) {}

            // Si no llegó mensaje del callback, registramos aviso y permitimos reintentar
            addDebugLog('Popup cerrado sin callback recibido.', 'warning');
            setConnectionStatus('idle');
            return;
          }

          // Fallback seguro: si el popup ya navegó a nuestro dominio de callback, leer código directamente
          try {
            const href = popup?.location?.href;
            const sameOrigin = typeof href === 'string' && href.startsWith(window.location.origin);
            if (sameOrigin && href.includes('/oauth/callback.html') && !authCompletedRef.current) {
              const url = new URL(href);
              const code = url.searchParams.get('code');
              const state = url.searchParams.get('state');
              if (code && state) {
                addDebugLog('Detected auth code via popup URL (fallback).', 'success');
                // Marcar como completado antes de procesar
                authCompletedRef.current = true;
                setAuthCompleted(true);
                // Limpiar timers
                clearInterval(checkInterval);
                clearTimeout(timeoutId);
                intervalRef.current = null; timeoutRef.current = null;
                // Procesar callback
                await handleOAuthCallback({ code, state, type: 'OAUTH_CALLBACK' });
                return;
              }
            }
          } catch (e) {
            // Acceso cross-origin mientras está en dominio de MercadoLibre; ignorar hasta que redireccione
          }

        } catch (error) {
          addDebugLog(`Error monitoring popup: ${error?.message}`, 'error');
          console.error('Error monitoring OAuth popup:', error);
        }
      }, 2000); // Check every 2 seconds instead of 1 second
  intervalRef.current = checkInterval;

    } catch (error) {
      addDebugLog(`OAuth flow initialization error: ${error?.message}`, 'error');
      setIsAuthenticating(false);
      setConnectionStatus('error');
      onAuthError(`Error al iniciar el proceso de autorización: ${error?.message}`);
    }
  };

  const retryAuthentication = () => {
    setRetryAttempts(prev => prev + 1);
    setConnectionStatus('idle');
    setDebugInfo([]);
    handleOAuthFlow();
  };

  const cancelAuthentication = () => {
    addDebugLog('Authentication cancelled by user', 'warning');
      // No cerrar automáticamente: el usuario puede cerrar manualmente si quiere
      // if (authWindow) { authWindow?.close(); setAuthWindow(null); }
    setIsAuthenticating(false);
    setConnectionStatus('idle');
    setRetryAttempts(0);
  };

  const getStatusMessage = () => {
    switch (connectionStatus) {
      case 'connecting':
        return { text: 'Esperando autorización en MercadoLibre...', type: 'warning' };
      case 'success':
        return { text: '¡Conexión establecida exitosamente!', type: 'success' };
      case 'error':
        return {
          text: retryAttempts < 2 ? 'Error en el proceso de conexión' : 'Error persistente en la conexión',
          type: 'error'
        };
      default:
        return null;
    }
  };

  const statusMessage = getStatusMessage();

  // Check for OAuth callback on mount (simpler approach)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const authCode = urlParams?.get('code');
    const state = urlParams?.get('state');
    const error = urlParams?.get('error');

    if (error || authCode) {
      addDebugLog(`OAuth callback detected on mount - processing...`, 'info');
      // Clean URL parameters
      window.history?.replaceState({}, document.title, window.location?.pathname);
    }

    // Cleanup on unmount
    return () => {
      if (authWindow) {
        addDebugLog('Component unmounting, cleaning up...', 'info');
        authWindow?.close();
      }
    };
  }, []);

  return (
  <div className="bg-surface border border-border rounded-lg p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
          <Icon name="Shield" size={20} className="text-accent" />
        </div>
        <div>
          <h3 className="font-semibold text-text-primary">Autenticación OAuth 2.0</h3>
          <p className="text-sm text-text-secondary">Autoriza el acceso a tu cuenta de MercadoLibre</p>
        </div>
      </div>

      <div className="space-y-4">
        {/* Instructions */}
        <div className="bg-muted rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Icon name="Lock" size={20} className="text-primary mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-text-primary mb-2">Proceso de Autorización</p>
              <ul className="list-disc list-inside space-y-1 text-text-secondary">
                <li>Se abrirá una ventana oficial de MercadoLibre</li>
                <li>Inicia sesión con tus credenciales</li>
                <li>Autoriza el acceso a tu cuenta</li>
                <li>La conexión se establecerá automáticamente al cerrar la ventana</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Status message */}
        {statusMessage && (
          <div className={`border rounded-lg p-4 ${
            statusMessage?.type === 'warning' ? 'bg-warning/10 border-warning/20' :
            statusMessage?.type === 'success'? 'bg-success/10 border-success/20' : 'bg-error/10 border-error/20'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                {statusMessage?.type === 'warning' && (
                  <div className="w-6 h-6 border-2 border-warning border-t-transparent rounded-full animate-spin"></div>
                )}
                {statusMessage?.type === 'success' && (
                  <Icon name="CheckCircle" size={20} className="text-success" />
                )}
                {statusMessage?.type === 'error' && (
                  <Icon name="AlertCircle" size={20} className="text-error" />
                )}
                <div>
                  <p className={`font-medium ${
                    statusMessage?.type === 'warning' ? 'text-warning' :
                    statusMessage?.type === 'success'? 'text-success' : 'text-error'
                  }`}>
                    {statusMessage?.text}
                  </p>
                  {statusMessage?.type === 'warning' && (
                    <p className="text-sm text-text-secondary">
                      Completa el proceso en la ventana de MercadoLibre.
                    </p>
                  )}
                </div>
              </div>
              {statusMessage?.type === 'warning' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={cancelAuthentication}
                  iconName="X"
                >
                  Cancelar
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Debug information panel */}
        {debugInfo?.length > 0 && (
          <div className="bg-muted/30 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <Icon name="Bug" size={16} className="text-primary" />
                <p className="text-sm font-medium text-text-primary">Estado de Conexión</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setDebugInfo([])}
                iconName="Trash2"
                className="text-xs"
              >
                Limpiar
              </Button>
            </div>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {debugInfo?.slice(-6)?.map((log, index) => (
                <div key={index} className="text-xs">
                  <span className="text-text-secondary">{log?.timestamp}</span>
                  <span className={`ml-2 ${
                    log?.type === 'success' ? 'text-success' :
                    log?.type === 'error' ? 'text-error' :
                    log?.type === 'warning' ? 'text-warning' : 'text-text-primary'
                  }`}>
                    {log?.message}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action buttons - SOLO LOS BOTONES REALES */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            variant="default"
            onClick={handleOAuthFlow}
            disabled={!isEnabled}
            iconName="ExternalLink"
            iconPosition="left"
            className="flex-1"
          >
            Conectar con MercadoLibre
          </Button>

          <Button
            variant="outline"
            onClick={() => onRefreshToken?.()}
            disabled={!isEnabled || isRefreshing}
            loading={isRefreshing}
            iconName="RefreshCw"
            iconPosition="left"
          >
            Refrescar Token
          </Button>
        </div>

        {!isEnabled && (
          <div className="text-sm text-text-secondary bg-muted/50 rounded-lg p-3">
            <Icon name="AlertCircle" size={16} className="inline mr-2" />
            Guarda las credenciales primero para habilitar la autenticación
          </div>
        )}
      </div>
    </div>
  );
};

export default OAuthFlow;